import { getKeycloakInstance } from "@auth/keycloakClient";
import { isKeycloakAuth, isLegacyAuth } from "@configs/authMode";
import { API_BASE_URL } from "@configs/runtimeConfig";
import i18n from "i18next";
import { getLocalStorage } from "@utils/local-storage";
import { LOCAL_STORAGE } from "@constants/index";
import { showInfoToast, showWarningToast } from "@configs/react-toastify/snackbar";

const isCurrentUserTargeted = (userIds) => {
    const currentUserId = getLocalStorage(LOCAL_STORAGE.USER_ID);
    if (!currentUserId || !Array.isArray(userIds)) return false;

    return userIds.some((userId) => String(userId) === String(currentUserId));
};

const recentProductManagementToasts = new Map();

const shouldShowProductManagementToast = (eventType, productName) => {
    const toastKey = `${eventType}:${productName}`;
    if (recentProductManagementToasts.has(toastKey)) {
        return false;
    }

    recentProductManagementToasts.set(toastKey, true);
    setTimeout(() => {
        recentProductManagementToasts.delete(toastKey);
    }, 3000);

    return true;
};

export const handleProductManagementNotification = (eventType, payload) => {
    const productName = typeof payload?.productName === "string" ? payload.productName.trim() : "";
    if (!productName || !isCurrentUserTargeted(payload?.userIds)) return;
    if (!shouldShowProductManagementToast(eventType, productName)) return;

    if (eventType === "CREATE") {
        showInfoToast(
            i18n.t("product.product-manager-assigned-title"),
            i18n.t("product.product-manager-assigned-message", { productName }),
        );
        return;
    }

    if (eventType === "DELETE") {
        showWarningToast(
            i18n.t("product.product-manager-removed-title"),
            i18n.t("product.product-manager-removed-message", { productName }),
        );
    }
};

const parseEventData = (raw) => {
  try {
    return JSON.parse(raw);
  } catch (error) {
    return null;
  }
};

const nextBackoffMs = (attempt) => {
  const base = Math.min(30_000, 1000 * 2 ** Math.max(0, attempt));
  const jitter = Math.floor(Math.random() * 500);
  return base + jitter;
};

export const ensureSseCookie = async () => {
  let token = null;

  if (isLegacyAuth()) {
    token = localStorage.getItem(LOCAL_STORAGE.TOKEN);
    if (!token) {
      return false;
    }
  } else if (isKeycloakAuth()) {
    const kc = getKeycloakInstance();
    if (!kc?.authenticated) {
      return false;
    }

    try {
      await kc.updateToken(30);
    } catch {
      return false;
    }
    token = kc.token;
    if (!token) {
      return false;
    }
  } else {
    return false;
  }

  const res = await fetch(`${API_BASE_URL}/sse/token`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
    },
    credentials: "include",
  });

  return res.ok;
};

/**
 * @param {(eventType: string, data: any) => void} onEventReceived
 * @param {{ onReconnect?: () => void }} [options]
 */
export const connectSSE = (onEventReceived, options = {}) => {
  let eventSource = null;
  let isClosed = false;
  let reconnectAttempt = 0;
  let reconnectTimer = null;
  let hadError = false;

  const clearReconnectTimer = () => {
    if (reconnectTimer) {
      clearTimeout(reconnectTimer);
      reconnectTimer = null;
    }
  };

  const connect = async () => {
    if (isClosed) return;

    try {
      await ensureSseCookie();
    } catch (e) {
      console.warn("Failed to mint SSE cookie:", e);
    }

    eventSource = new EventSource(`${API_BASE_URL}/sse/connect`, { withCredentials: true });

    eventSource.onopen = () => {
      const shouldReconcile = hadError;
      hadError = false;
      reconnectAttempt = 0;
      if (shouldReconcile && typeof options.onReconnect === "function") {
        options.onReconnect();
      }
    };

    const forward = (eventType) => (event) => {
      const parsedData = parseEventData(event.data);
      if (parsedData == null) return;
      if (eventType === "CREATE" || eventType === "DELETE") {
        handleProductManagementNotification(eventType, parsedData);
      }
      onEventReceived(eventType, parsedData);
    };

    eventSource.addEventListener("CREATE", forward("CREATE"));
    eventSource.addEventListener("UPDATE", forward("UPDATE"));
    eventSource.addEventListener("DELETE", forward("DELETE"));
    eventSource.addEventListener("UPDATE_ALL", forward("UPDATE_ALL"));
    eventSource.addEventListener("FOSSID_IMPORT", forward("FOSSID_IMPORT"));
    eventSource.addEventListener("GENESIS_IMPORT", forward("GENESIS_IMPORT"));
    eventSource.addEventListener("AUTHORIZATION_CHANGED", forward("AUTHORIZATION_CHANGED"));
    eventSource.addEventListener("ACCOUNT_INACTIVE", forward("ACCOUNT_INACTIVE"));
    eventSource.addEventListener("CONNECT", forward("CONNECT"));
    eventSource.addEventListener("HEARTBEAT", forward("HEARTBEAT"));

    eventSource.addEventListener("PASSWORD_RESET", (event) => {
      const parsedData = JSON.parse(event.data);
      onEventReceived("PASSWORD_RESET", parsedData);
    });

    eventSource.onerror = () => {
      console.error("SSE error, reconnecting...");
      hadError = true;
      try {
        eventSource?.close();
      } catch {
        /* noop */
      }
      if (!isClosed) {
        clearReconnectTimer();
        const delay = nextBackoffMs(reconnectAttempt);
        reconnectAttempt += 1;
        reconnectTimer = setTimeout(connect, delay);
      }
    };

    return eventSource;
  };

  connect();

  return {
    close: () => {
      isClosed = true;
      clearReconnectTimer();
      try {
        eventSource?.close();
      } catch {
        /* noop */
      }
    },
  };
};

export const disconnectSSE = (eventSource) => {
  if (eventSource) {
    eventSource.close();
  }
};